import React, { useEffect, useMemo, useState, useCallback } from 'react';
import EntityTable from '../components/EntityTable';
import EntityDetailPanel from '../components/EntityDetailPanel';
import Toolbar from '../components/Toolbar';
import RelLink from '../components/RelLink';
import { ImageWithHoverPreview } from '../../components/CardHoverPreview/CardHoverPreview';
import { Listings, Users } from '../api/admin';

// Opens on every broker (accounts with the Broker role, from GET /users) with
// their listing and order counts. Click a broker to see their active listings
// with cert images — the same inventory they see in their own portal.
const BrokersTab = ({ filters, goTo }) => {
    const [email, setEmail] = useState(filters.brokerEmail || '');
    const [pendingEmail, setPendingEmail] = useState(filters.brokerEmail || '');
    const [listings, setListings] = useState([]);
    const [brokers, setBrokers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [selected, setSelected] = useState(null);

    useEffect(() => {
        setEmail(filters.brokerEmail || '');
        setPendingEmail(filters.brokerEmail || '');
    }, [filters.brokerEmail]);

    const load = useCallback(async () => {
        if (!email) {
            setListings([]);
            setLoading(true);
            setError(null);
            try {
                const result = await Users.list();
                setBrokers((result.users || []).filter((u) => (u.roles || []).includes('Broker')));
            } catch (e) {
                setError(e.message);
                setBrokers([]);
            } finally {
                setLoading(false);
            }
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const l = await Listings.list({ sellerEmail: email, count: 1000 }).catch(() => ({ listings: [] }));
            setListings(l.listings || []);
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    }, [email]);

    useEffect(() => { load(); }, [load]);

    const submit = () => goTo({ tab: 'brokers', brokerEmail: pendingEmail || undefined });

    // While no broker is open, the search box filters the broker list as you type.
    const visibleBrokers = useMemo(() => {
        const q = pendingEmail.trim().toLowerCase();
        return q ? brokers.filter((b) => b.email.toLowerCase().includes(q)) : brokers;
    }, [brokers, pendingEmail]);

    const countLink = (n, tab, linkFilters) =>
        n > 0 ? (
            <RelLink tab={tab} filters={linkFilters} goTo={goTo} className="users-count">{n}</RelLink>
        ) : <span className="users-count users-count-zero">0</span>;

    return (
        <div className="admin-tab-panel">
            <Toolbar>
                <input
                    placeholder={email ? 'Broker email' : 'Search brokers by email'}
                    value={pendingEmail}
                    onChange={(e) => setPendingEmail(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && submit()}
                />
                {email ? (
                    <button className="btn-ghost" onClick={() => goTo({ tab: 'brokers' })}>‹ All Brokers</button>
                ) : (
                    <>
                        {pendingEmail && <button className="btn-ghost" onClick={() => setPendingEmail('')}>Clear</button>}
                        <span className="users-stat">
                            {visibleBrokers.length} of {brokers.length} broker{brokers.length === 1 ? '' : 's'}
                        </span>
                    </>
                )}
            </Toolbar>

            {loading && <p className="loading">Loading…</p>}
            {error && <p className="error-message">Error: {error}</p>}

            {!email && !loading && !error && (
                <EntityTable
                    columns={[
                        {
                            key: 'email',
                            header: 'Broker',
                            render: (b) => <span className="users-email">{b.email}</span>,
                        },
                        {
                            key: 'active_listings',
                            header: 'Active Listings',
                            width: '130px',
                            render: (b) => <span className="users-count">{b.active_listings}</span>,
                        },
                        {
                            key: 'orders_sold',
                            header: 'Orders Sold',
                            width: '120px',
                            render: (b) => countLink(b.orders_sold, 'orders', { sellerEmail: b.email }),
                        },
                    ]}
                    rows={visibleBrokers}
                    rowKey="email"
                    onRowClick={(b) => goTo({ tab: 'brokers', brokerEmail: b.email })}
                    emptyMessage={pendingEmail ? `No broker matches "${pendingEmail}".` : 'No accounts have the Broker role yet.'}
                />
            )}

            {email && !loading && !error && (
                <>
                    <h3 className="section-title">
                        Listings by{' '}
                        <RelLink tab="users" filters={{ email }} goTo={goTo} title="Open this user">{email}</RelLink>
                        <span className="badge">{listings.length}</span>
                        <span className="brokers-links">
                            <RelLink tab="orders" filters={{ sellerEmail: email }} goTo={goTo}>Orders Sold</RelLink>
                            <RelLink tab="listings" filters={{ sellerEmail: email }} goTo={goTo}>Listings Tab</RelLink>
                        </span>
                    </h3>
                    <EntityTable
                        columns={[
                            {
                                key: 'image',
                                header: 'Img',
                                width: '76px',
                                render: (r) =>
                                    r.cert?.cert_images?.front_image_url ? (
                                        <ImageWithHoverPreview
                                            src={r.cert.cert_images.front_image_url}
                                            alt={r.cert?.name ? `Front: ${r.cert.name}` : 'Cert front'}
                                            className="cert-image"
                                        />
                                    ) : (
                                        <span className="cert-image-placeholder" aria-hidden="true">—</span>
                                    ),
                            },
                            {
                                key: 'cert_number',
                                header: 'Cert',
                                render: (r) =>
                                    r.cert_number ? (
                                        <RelLink tab="certs" filters={{ certNumber: r.cert_number }} goTo={goTo}>
                                            {r.cert_number}
                                        </RelLink>
                                    ) : '—',
                            },
                            { key: 'name', header: 'Card', render: (r) => r.cert?.name || '—' },
                            { key: 'grade', header: 'Grade', width: '70px', render: (r) => r.cert?.grade?.match(/\d+/)?.[0] || '—' },
                            { key: 'set', header: 'Set', render: (r) => r.cert?.card?.set?.name || '—' },
                            {
                                key: 'listing_id',
                                header: 'Listing #',
                                render: (r) =>
                                    r.listing_id ? (
                                        <RelLink tab="listings" filters={{ listingId: r.listing_id }} goTo={goTo}>
                                            {r.listing_id}
                                        </RelLink>
                                    ) : '—',
                            },
                            { key: 'price', header: 'Price', render: (r) => (r.price !== undefined ? `$${Number(r.price).toFixed(2)}` : '—') },
                            { key: 'auto_price', header: 'Auto', width: '60px', render: (r) => (r.auto_price ? '✅' : '—') },
                        ]}
                        rows={listings}
                        rowKey={(r) => r.id}
                        onRowClick={(r) => setSelected(r)}
                        emptyMessage="No listings for this broker."
                    />

                    <EntityDetailPanel
                        open={!!selected}
                        title={selected ? `Listing ${selected.listing_id || selected.id}` : ''}
                        onClose={() => setSelected(null)}
                        sections={selected ? [{
                            title: 'Listing',
                            rows: [
                                { label: 'Listing #', value: selected.listing_id },
                                {
                                    label: 'Cert',
                                    render: () =>
                                        selected.cert_number ? (
                                            <RelLink tab="certs" filters={{ certNumber: selected.cert_number }} goTo={goTo}>
                                                {selected.cert_number}
                                            </RelLink>
                                        ) : '—',
                                },
                                { label: 'Price', value: selected.price !== undefined ? `$${Number(selected.price).toFixed(2)}` : '—' },
                                { label: 'Active', value: selected.is_active ? 'Yes' : 'No' },
                                {
                                    label: 'Open in Listings tab',
                                    render: () => (
                                        <RelLink tab="listings" filters={{ listingId: selected.listing_id }} goTo={goTo}>
                                            Go to listing →
                                        </RelLink>
                                    ),
                                },
                            ],
                        }] : []}
                    />
                </>
            )}
        </div>
    );
};

export default BrokersTab;
